import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View, Platform } from 'react-native';
import Animated from 'react-native-reanimated';

import { theme } from '@sg/garnish';
import { PermissionsPrompt } from '@sg/shared-fe-features-permissions';

import {
  useBackgroundLocationPermissionPrimer,
  useLocationPermissionPrimer,
  usePushNotificationPermissionPrimer,
} from '../../hooks';
import {
  PermissionsPrimerBackgroundLocationView,
  PermissionsPrimerLocationView,
  PermissionsPrimerPushView,
} from './components';
import { usePermissionsPrimerAnimations } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPrimer = (props: Props) => {
  const { pushPrimer, locationPrimer, backgroundLocationPrimer } = props;

  const { formatMessage } = useIntl();

  // ─── Phase ───────────────────────────────────────────────────────────

  const isPushPhase = pushPrimer.isPrimerActive;
  const isLocationPhase = !isPushPhase && locationPrimer.isPrimerActive;
  const isBackgroundLocationPhase = !isPushPhase && !isLocationPhase && backgroundLocationPrimer?.isPrimerActive === true; // prettier-ignore
  const isPushRequesting = pushPrimer.isRequestingPushNotificationPermission;

  const shouldShowHexagon = !isBackgroundLocationPhase;
  const shouldShowLocationStrokes = !isPushPhase && !isBackgroundLocationPhase;

  // ─── Animations ──────────────────────────────────────────────────────

  const {
    hexagonScaleSV,
    hexagonRotateSV,
    appIconOpacitySV,
    contentOpacitySV,
    hexagonAnchorAnimatedStyles,
    hexagonEntering,
    hexagonLogoEntering,
    appIconEntering,
    contentEnteringDelay,
    locationStrokes,
    handlePrimerLayout,
  } = usePermissionsPrimerAnimations({ isPushPhase, isPushRequesting });

  const hexagonAnchorStyles = [styles.hexagonAnchor, hexagonAnchorAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <PermissionsPrompt.Container>
      <View style={StyleSheet.absoluteFill} pointerEvents="none" onLayout={handlePrimerLayout}>
        {shouldShowHexagon ? (
          <Animated.View style={hexagonAnchorStyles}>
            <PermissionsPrompt.Hexagon
              scaleSV={hexagonScaleSV}
              rotateSV={hexagonRotateSV}
              entering={hexagonEntering}
              hideLogo={isPushPhase}
              logoEntering={hexagonLogoEntering}
            />

            {isPushPhase ? (
              <View style={styles.appIconAnchor}>
                <PermissionsPrompt.AppIcon
                  entering={appIconEntering}
                  opacitySV={appIconOpacitySV}
                />
              </View>
            ) : null}
          </Animated.View>
        ) : null}

        {shouldShowLocationStrokes ? (
          <View style={styles.strokesContainer}>
            {locationStrokes.map((stroke, index) => (
              <PermissionsPrompt.HexagonStroke
                key={`primer-hexagon-stroke-${index}`}
                scale={stroke.scale}
                delay={stroke.delay}
                hideActiveOverlay={stroke.hideActiveOverlay}
              />
            ))}
          </View>
        ) : null}

        {isBackgroundLocationPhase ? <PermissionsPrompt.HexagonGlass /> : null}
      </View>

      {isBackgroundLocationPhase ? (
        <View style={styles.notificationsContainer} pointerEvents="none">
          {NOTIFICATION_CARDS.map((card, index) => (
            <View
              key={`primer-notification-card-${index}`}
              style={
                index === NOTIFICATION_CARD_FOCUSED_INDEX
                  ? styles.notificationFocused
                  : styles.notification
              }
            >
              <PermissionsPrompt.NotificationCard
                title={formatMessage(card.title)}
                time={formatMessage(card.time)}
                isPulsing={index === NOTIFICATION_CARD_FOCUSED_INDEX}
                isMuted={index !== NOTIFICATION_CARD_FOCUSED_INDEX}
              />
            </View>
          ))}
        </View>
      ) : null}

      <PermissionsPrompt.ContentContainer
        enteringDelay={contentEnteringDelay}
        opacitySV={contentOpacitySV}
      >
        {isPushPhase ? (
          <PermissionsPrimerPushView
            isRequesting={pushPrimer.isRequestingPushNotificationPermission}
            isSkipping={pushPrimer.isSkippingPushNotificationPermission}
            onAllow={pushPrimer.onAllow}
            onSkip={pushPrimer.onSkip}
          />
        ) : null}

        {isLocationPhase ? (
          <PermissionsPrimerLocationView
            isRequesting={locationPrimer.isRequestingLocationPermission}
            isSkipping={locationPrimer.isSkippingLocationPermission}
            onAllow={locationPrimer.onAllow}
            onSkip={locationPrimer.onSkip}
          />
        ) : null}

        {isBackgroundLocationPhase && backgroundLocationPrimer ? (
          <PermissionsPrimerBackgroundLocationView
            isRequesting={backgroundLocationPrimer.isRequestingBackgroundLocationPermission}
            isSkipping={backgroundLocationPrimer.isSkippingBackgroundLocationPermission}
            onAllow={backgroundLocationPrimer.onAllow}
            onSkip={backgroundLocationPrimer.onSkip}
          />
        ) : null}
      </PermissionsPrompt.ContentContainer>
    </PermissionsPrompt.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  noLineTitle: {
    defaultMessage: 'No line right now',
    description: 'Permissions Primer | Background Location | No line notification title',
  },
  noLineTime: {
    defaultMessage: '1h ago',
    description: 'Permissions Primer | Background Location | No line notification timestamp',
  },
  scanTitle: {
    defaultMessage: 'Hungry? Tap to scan',
    description: 'Permissions Primer | Background Location | Scan notification title',
  },
  scanTime: {
    defaultMessage: 'Now',
    description: 'Permissions Primer | Background Location | Scan notification timestamp',
  },
  freeDrinkTitle: {
    defaultMessage: 'Free drink on us!',
    description: 'Permissions Primer | Background Location | Free drink notification title',
  },
  freeDrinkTime: {
    defaultMessage: '2h ago',
    description: 'Permissions Primer | Background Location | Free drink notification timestamp',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const NOTIFICATION_CARDS = [
  { title: messages.noLineTitle, time: messages.noLineTime },
  { title: messages.scanTitle, time: messages.scanTime },
  { title: messages.freeDrinkTitle, time: messages.freeDrinkTime },
];

const NOTIFICATION_CARD_FOCUSED_INDEX = 1;
const NOTIFICATION_CARD_RESTING_SCALE = 0.9;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  notificationsContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingTop: theme.spacing['18'],
    paddingHorizontal: theme.spacing['6'],
  },
  notification: {
    transform: [{ scale: NOTIFICATION_CARD_RESTING_SCALE }],
  },
  notificationFocused: {
    zIndex: 2,
    marginVertical: Platform.select({ android: -theme.spacing['1'], default: -theme.spacing['4'] }),
  },
  hexagonAnchor: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  appIconAnchor: {
    position: 'absolute',
    top: 460,
    left: 320,
  },
  strokesContainer: {
    position: 'absolute',
    top: '10%',
    left: 0,
    right: 0,
    bottom: '30%',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  pushPrimer: ReturnType<typeof usePushNotificationPermissionPrimer>;
  locationPrimer: ReturnType<typeof useLocationPermissionPrimer>;
  backgroundLocationPrimer?: ReturnType<typeof useBackgroundLocationPermissionPrimer>;
};
